import { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { FEATURES } from '@makemydeal/dr-sales-checkin-types';
import { TRADE_VEHICLE_INFO, updateTradeInCurrentLocation } from '@makemydeal/dr-activities-tradein';
import { getTradeInOverview, isFullPageScrollEnabled, removeTradeInFromSection } from '@makemydeal/dr-sales-checkin-store';
import { useOpenTakeover } from './useOpenTakeover';
import { formatTradeInMileage, formatTradeInAmount } from '@makemydeal/dr-sales-checkin-utils';
import { TRADE_IN_RECORD_CONTENT } from '../constants';
import { useTheme } from 'styled-components';
import type { CombinedTheme } from '../types';

export const useTradeInOverview = () => {
    const dispatch = useDispatch();
    const enableFullPageScroll = useSelector(isFullPageScrollEnabled);
    const tradeIn = useSelector(getTradeInOverview, shallowEqual);
    const [showRemoveTradeInConfirm, setShowRemoveTradeInConfirm] = useState(false);
    const openTakeover = useOpenTakeover();

    const theme = useTheme() as CombinedTheme;
    const valueColor = theme.uiBricks?.secondaryFontColor;
    const isAppraised = tradeIn?.appraisalStatus === 'APPRAISED';
    const { emptyValue } = TRADE_IN_RECORD_CONTENT;

    const fields = [
        {
            label: TRADE_IN_RECORD_CONTENT.appraisalStatusLabel,
            value: isAppraised ? TRADE_IN_RECORD_CONTENT.appraised : TRADE_IN_RECORD_CONTENT.pendingAppraisal,
            color: isAppraised ? theme.tokens.BaseColorGreen500 : valueColor,
            testId: 'trade-in-record-status'
        },
        {
            label: TRADE_IN_RECORD_CONTENT.mileageLabel,
            value: formatTradeInMileage(tradeIn?.mileage, emptyValue),
            color: valueColor,
            testId: 'trade-in-record-mileage'
        },
        {
            label: TRADE_IN_RECORD_CONTENT.estimatedValueLabel,
            value: formatTradeInAmount(tradeIn?.estimatedValue, emptyValue),
            color: valueColor,
            testId: 'trade-in-record-value'
        },
        {
            label: TRADE_IN_RECORD_CONTENT.payoffLabel,
            value: formatTradeInAmount(tradeIn?.payoff, emptyValue),
            color: valueColor,
            testId: 'trade-in-record-payoff'
        }
    ];

    const onStart = () => {
        openTakeover(FEATURES.TRADE_IN);
    };

    return {
        tradeIn: enableFullPageScroll ? { data: tradeIn, fields, isAppraised, valueColor, emptyValue } : undefined,
        showRemoveTradeInConfirm,
        onStart,
        onEdit: () => {
            dispatch(updateTradeInCurrentLocation(TRADE_VEHICLE_INFO));
            onStart();
        },
        onRemove: () => setShowRemoveTradeInConfirm(true),
        onCancelRemove: () => setShowRemoveTradeInConfirm(false),
        onConfirmRemove: () => {
            setShowRemoveTradeInConfirm(false);
            dispatch(removeTradeInFromSection());
        }
    };
};
